// CountyFipsContext.tsx
import { createContext, useState, useEffect, useContext } from "react";
import { fetchCountyFips } from "../utils/dashboardUtils";
import { useOrgContext } from "./OrgIdContext";
import { GeoTag } from "../services/types";

// Define context type
interface CountyFipsContextType {
  countyFips: GeoTag | null;
  loading: boolean;
}

const CountyFipsContext = createContext<CountyFipsContextType>({
  countyFips: null,
  loading: false,
});

export const CountyFipsProvider = ({ children }: { children: React.ReactNode }) => {
  const { locationId } = useOrgContext();
  const [countyFips, setCountyFips] = useState<GeoTag | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [overrideFips, setOverrideFips] = useState<string | null>(null);

  // Listen for update messages with county info
  useEffect(() => {
    const receiveMessage = (event: MessageEvent) => {
      if (event.data?.message === "update") {
        const counties = event.data.params?.area_view?.geo_details?.find(
          (x: any) => x.lod === "county"
        );
        if (counties?.ids?.length) {
          console.log("Received override counties from message:", counties.ids);
          // We'll just take the first county ID for now (can be extended)
          setOverrideFips(counties.ids[0]);
        }
      }
    };

    window.addEventListener("message", receiveMessage);
    return () => {
      window.removeEventListener("message", receiveMessage);
    };
  }, []);

  // Fetch or use override FIPS
  useEffect(() => {
    const fetchData = async () => {
      if (!locationId) return;

      setLoading(true);
      try {
        if (overrideFips) {
          console.log("Using override countyFips:", overrideFips);
          setCountyFips({ value: overrideFips, type: 'county' });
        } else {
          const fips = await fetchCountyFips(locationId);
          console.log("Fetched countyFips from API:", fips?.value);
          setCountyFips(fips || null);
        }
      } catch (error) {
        console.error("Error fetching countyFips:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [locationId, overrideFips]);

  return (
    <CountyFipsContext.Provider value={{ countyFips, loading }}>
      {children}
    </CountyFipsContext.Provider>
  );
};

// Hook to use the context
export const useCountyFips = () => useContext(CountyFipsContext);

export default CountyFipsContext;
